import React, { useState, useEffect, useRef } from 'react';
import { t } from '../i18n';
import { useParams } from 'react-router-dom';
import { sheetAPI, passportAuth } from '../api';
import { useTheme } from '../contexts/ThemeContext';

function InfoRow({ label, value, color }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      {/* Ширина подписи ОДНА на все строки: «Температура в производственном
          помещении» раньше растягивала свою строку, и значение уезжало из
          общей колонки. Теперь длинная подпись переносится. */}
      <span style={{ color: 'var(--dim)', fontSize: 13, flex: '0 0 44%' }}>{label}</span>
      <span style={{ color: color || 'var(--text)', fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0 }}>{value}</span>
    </div>
  );
}

// Смена в паспорте — номером: так её называют в документах заказчика.
const SHIFT_NO = { day: 'Смена 1', night: 'Смена 2' };

function formatDuration(seconds) {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m} мин ${s} сек` : `${s} сек`;
}

export default function SheetQR() {
  const { qrCode } = useParams();
  const [qr,      setQr]      = useState('');
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const inputRef = useRef(null);
  const { isDark, toggle } = useTheme();
  // Пароль к закрытой части. Помним на вкладку, чтобы следующий лист не
  // спрашивал его снова, — но не дольше: телефон бывает чужим.
  const [keyInput, setKeyInput] = useState('');
  const [showKey, setShowKey] = useState(false);
  // Вход сотрудника прямо на странице: запоминается на телефоне (localStorage),
  // чтобы каждый новый скан по QR открывал паспорт целиком.
  const [who, setWho] = useState(passportAuth.user());
  const [showLogin, setShowLogin] = useState(false);
  const [login, setLogin] = useState('');
  const [pass, setPass] = useState('');
  const [loginErr, setLoginErr] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);

  useEffect(() => {
    if (qrCode) { setQr(qrCode); fetchSheet(qrCode); }
    else inputRef.current?.focus();
  }, [qrCode]); // eslint-disable-line

  const fetchSheet = async (code) => {
    if (!code) return;
    setLoading(true);
    setError('');
    setData(null);
    try {
      let key = '';
      try { key = sessionStorage.getItem('passport_key') || ''; } catch (e) { /* нет хранилища */ }
      const r = await sheetAPI.getByQr(code.trim(), key);
      // Неверный пароль не держим: иначе каждый следующий скан тратил бы
      // попытку и через десять листов упёрся бы в паузу.
      if (r.data?.key_error) {
        try { sessionStorage.removeItem('passport_key'); } catch (e) { /* нет */ }
      }
      setData(r.data);
    } catch (e) {
      setError('Лист не найден. Проверьте код.');
    } finally { setLoading(false); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchSheet(qr);
  };

  const unlock = (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;
    try { sessionStorage.setItem('passport_key', keyInput.trim()); } catch (err) { /* нет */ }
    setKeyInput('');
    fetchSheet(data?.code || qr || qrCode);
  };

  const doLogin = async (e) => {
    e.preventDefault();
    if (!login.trim() || !pass) return;
    setLoginBusy(true);
    setLoginErr('');
    try {
      await passportAuth.login(login.trim(), pass);
      setPass('');
      setLogin('');
      setWho(passportAuth.user());
      setShowLogin(false);
      const code = data?.code || qr || qrCode;
      if (code) fetchSheet(code);
    } catch (err) {
      const st = err.response?.status;
      setLoginErr(err.code === 'service'
        ? t('Это служебная учётка обмена с 1С. Входа в программу у неё нет.')
        : st === 400 || st === 401 ? t('Неверный логин или пароль') : t('Нет связи с сервером. Попробуйте ещё раз'));
    } finally { setLoginBusy(false); }
  };

  const doLogout = () => {
    passportAuth.logout();
    setWho('');
    const code = data?.code || qr || qrCode;
    if (code) fetchSheet(code);
  };

  const inputSt = { width: '100%', boxSizing: 'border-box', padding: '14px 14px', minHeight: 48, background: 'var(--input)', border: '1px solid var(--border)', borderRadius: 10, color: 'var(--text)', fontSize: 16, outline: 'none' };
  const job = data?.press_job;
  const gradeColor = data?.is_default ? '#22c55e' : (data?.grade_type?.includes('2') ? '#f59e0b' : '#ef4444');

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ background: 'var(--card)', borderBottom: '1px solid var(--border)', padding: '0 12px', height: 56, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 20 }}>📋</span>
        <span style={{ fontWeight: 700, fontSize: 16 }}>{t('Паспорт изделия')}</span>
        {/* Паспорт открывают и в цеху с планшета, и на улице с телефона:
            днём тёмная страница на солнце не читается, ночью светлая слепит.
            Переключатель тут же, а не в настройках программы, — сюда заходят
            по QR, минуя вход, и никаких настроек у человека нет. */}
        <button onClick={toggle} title={isDark ? t('Светлая тема') : t('Тёмная тема')}
          style={{
            marginLeft: 'auto', width: 34, height: 34, borderRadius: 9,
            border: '1px solid var(--border)', background: 'var(--bg)',
            color: 'var(--muted)', cursor: 'pointer', fontSize: 15,
            display: 'grid', placeItems: 'center', fontFamily: 'inherit',
          }}>{isDark ? '☀' : '☾'}</button>
        {who ? (
          <>
            <span style={{ color: 'var(--muted)', fontSize: 13, maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={who}>{who}</span>
            <button onClick={doLogout} style={{ minHeight: 40, padding: '0 12px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>{t('Выйти')}</button>
          </>
        ) : (
          <button onClick={() => setShowLogin(v => !v)} style={{ minHeight: 40, padding: '0 14px', borderRadius: 9, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{t('Войти')}</button>
        )}
      </div>

      <div style={{ maxWidth: 540, margin: '0 auto', padding: '28px 16px' }}>
        {showLogin && !who && (
          <form onSubmit={doLogin} style={{ background: 'var(--card)', borderRadius: 16, padding: 20, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{t('Вход сотрудника')}</div>
            <div style={{ color: 'var(--dim)', fontSize: 12.5 }}>{t('Логин и пароль от MES. Вход запомнится на этом телефоне.')}</div>
            <input value={login} onChange={e => setLogin(e.target.value)} placeholder={t('Логин')}
              autoComplete="username" autoCapitalize="none" autoCorrect="off" style={inputSt} />
            <input type="password" value={pass} onChange={e => setPass(e.target.value)} placeholder={t('Пароль')}
              autoComplete="current-password" style={inputSt} />
            {loginErr && <div style={{ color: 'var(--danger)', fontSize: 14 }}>{loginErr}</div>}
            <button type="submit" disabled={!login.trim() || !pass || loginBusy}
              style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, minHeight: 48, fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              {loginBusy ? '...' : t('Войти')}
            </button>
          </form>
        )}
        {/* QR kiritish */}
        {!data && (
          <div style={{ background: 'var(--card)', borderRadius: 16, padding: 28, textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 56, marginBottom: 12 }}>📷</div>
            <div style={{ color: 'var(--muted)', fontSize: 15, marginBottom: 20 }}>
              Отсканируйте QR листа или введите код
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 10 }}>
              <input
                ref={inputRef}
                value={qr}
                onChange={e => setQr(e.target.value)}
                placeholder={t("QR-код или код листа")}
                style={{ flex: 1, padding: '12px 14px', background: 'var(--input)', border: '1px solid var(--border)', borderRadius: 10, color: 'var(--text)', fontSize: 14, outline: 'none' }}
                autoComplete="off"
              />
              <button type="submit" disabled={!qr || loading} style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                {loading ? '...' : 'Найти'}
              </button>
            </form>
            {error && <div style={{ color: 'var(--danger)', marginTop: 14, fontSize: 14 }}>{error}</div>}
          </div>
        )}

        {/* Ma'lumot */}
        {data && (
          <div>
            {/* Sarlavha.
                Бирка может пережить лист: пресс сработал вхолостую, выпуск
                смены поправили вниз, смена закрылась раньше, чем кончилась
                пачка. Раньше на такой код страница отвечала «лист не найден»,
                и человек с бумагой в руках считал это поломкой программы.
                Теперь бирка называет себя сама: чей пресс, какое задание,
                когда вышла и почему листа за ней нет. */}
            {data.label_only ? (
              <div style={{
                background: 'var(--card)', borderRadius: 16, padding: '20px 24px',
                marginBottom: 16, borderTop: '4px solid #f59e0b',
              }}>
                <div style={{ color: '#b45309', fontSize: 18, fontWeight: 900 }}>
                  🏷 {data.state_title}
                </div>
                <div style={{ color: 'var(--muted)', fontSize: 13.5, marginTop: 8, lineHeight: 1.55 }}>
                  {data.state_why}
                </div>
                {/* Бирка без листа: изделие и код НКТ берём с задания, на которое она выдана. */}
                {data.product_name !== '—' && (
                  <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 10 }}>
                    Изделие: <b style={{ color: 'var(--muted)' }}>{data.product_name}</b>
                    {' · '}Код НКТ: <b style={{ color: 'var(--muted)' }}>{data.nkt_code || '—'}</b>
                  </div>
                )}
                <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 12 }}>
                  Код: <b style={{ color: 'var(--muted)', fontFamily: 'monospace' }}>{data.code}</b>
                  {data.issued_at && (
                    <> · выдана {new Date(data.issued_at).toLocaleString('ru-RU', {
                      day: '2-digit', month: '2-digit', year: 'numeric',
                      hour: '2-digit', minute: '2-digit' })}</>
                  )}
                </div>
              </div>
            ) : (
            // Про счёт смены страница молчит. Её открывают с биркой в руках —
            // у стопки, в машине, у клиента, — и человеку нужен паспорт листа,
            // а не бухгалтерия цеха. Счёт поправят там, где считают.
            <div style={{ background: 'var(--card)', borderRadius: 16, padding: '20px 24px', marginBottom: 16, textAlign: 'center' }}>
              {/* Заказчик просил заголовок и на самом документе: полоса сверху
                  при печати и на скриншоте теряется, а бумага без названия —
                  не паспорт. */}
              <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 14 }}>
                {t('Паспорт изделия')}
              </div>
              <div style={{ background: `${gradeColor}20`, border: `2px solid ${gradeColor}50`, borderRadius: 10, padding: '10px 20px', display: 'inline-block' }}>
                <span style={{ color: gradeColor, fontSize: 22, fontWeight: 900 }}>{data.grade_type}</span>
              </div>
              <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 10 }}>
                Код: <b style={{ color: 'var(--muted)' }}>{data.code || data.qr_short}</b>
              </div>
              {(data.grade_history || []).length > 0 && (
                <div style={{ color: '#f59e0b', fontSize: 12, marginTop: 8 }}>
                  ⚠ Сорт менялся — на бирке напечатан прежний
                </div>
              )}
            </div>
            )}

            {/* Пересортица. Бирку печатают высшим сортом, ещё до того как
                известно, какой лист бракованный, — сорт перебивает склад.
                Поэтому по QR всегда должно быть видно, кто и когда его менял:
                иначе бумага и база расходятся без объяснений. */}
            {(data.grade_history || []).length > 0 && (
              <div style={{ background: 'var(--card)', borderRadius: 14, padding: '16px 20px', marginBottom: 12 }}>
                <div style={{ color: '#f59e0b', fontSize: 12, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  🏷 Изменение сорта
                </div>
                {data.grade_history.map(h => (
                  <div key={h.id} style={{
                    borderLeft: `2px solid ${h.is_revert ? 'var(--dim)' : '#f59e0b'}`,
                    paddingLeft: 10, marginBottom: 10,
                  }}>
                    <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 700 }}>
                      {h.from} → {h.to}
                      {h.is_revert && (
                        <span style={{ color: 'var(--dim)', fontSize: 12, fontWeight: 400 }}> · отмена</span>
                      )}
                    </div>
                    <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 2 }}>
                      {h.by} · {new Date(h.at).toLocaleString('ru-RU', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit' })}
                      {h.reason ? ` · ${h.reason}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Приёмка */}
            {!data.label_only && (
            <div style={{ background: 'var(--card)', borderRadius: 14, padding: '16px 20px', marginBottom: 12 }}>
              <div style={{ color: '#f59e0b', fontSize: 12, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>✅ Приёмка</div>
              <InfoRow label="Принял (контролёр)" value={data.accepted_by} color="#22c55e" />
              <InfoRow label="Время приёмки" value={data.accepted_at ? new Date(data.accepted_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null} />
            </div>
            )}

            {/* Производство */}
            {job && (
              <div style={{ background: 'var(--card)', borderRadius: 14, padding: '16px 20px', marginBottom: 12 }}>
                <div style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>🏭 Производство</div>
                <InfoRow label="Пресс" value={job.press_name} />
                <InfoRow label="Оператор пресса" value={job.operator} color="#a78bfa" />
                <InfoRow label="Дата" value={job.started_at ? new Date(job.started_at).toLocaleDateString('ru-RU') : null} />
                {/* В паспорте смена — номером, как в документах заказчика.
                    В цехе она по-прежнему «День/Ночь»: меняется только бумага. */}
                <InfoRow label="Смена" value={SHIFT_NO[job.shift] || job.shift_display} />
                <InfoRow label="Текстура" value={job.texture} />
                <InfoRow label="Толщина плиты" value={job.thickness ? `${job.thickness} мм` : null} />
                {job.product_name !== '—' && <InfoRow label="Изделие" value={job.product_name} />}
                {/* Код НКТ — всегда строкой, даже пустой: графа обязательная, и
                    её отсутствие на бумаге выглядело бы как забытая, а не как
                    «в 1С не заполнено». */}
                {job.product_name !== '—' && <InfoRow label="Код НКТ" value={job.nkt_code || '—'} />}
                {/* Заказчик — только в закрытой части: сервер не пришлёт его тому,
                    кому она не открыта. */}
                {job.client_name && <InfoRow label="Заказчик" value={job.client_name} />}
                {job.order_number !== '—' && <InfoRow label="Заказ №" value={job.order_number} color="#f59e0b" />}
              </div>
            )}

            {/* Режим прессования — отдельным блоком.
                Раньше здесь стояли только температура и выдержка, а давление,
                воздух в цехе и влажность не показывались вовсе: при разборе
                брака половину ответа приходилось искать в другом экране.
                И это режим ЭТОГО листа: станок отдаёт показание на каждое
                прессование, а смена идёт восемь часов — за это время плита
                уходит, и утренние цифры к сороковому листу уже не про него. */}
            {/* Закрытая часть (режим, материал, заказчик) приходит с сервера
                только своим: вошедшим в MES или знающим пароль цеха. Чужому
                её просто нет — ни замка, ни надписи «закрыто»: паспорт
                выглядит законченным документом, и мысли, что от читателя
                что-то спрятали, у него возникать не должно. Пароль вводят
                через «Вход для сотрудников» внизу. */}
            {job && !data.locked && (
              <div style={{ background: 'var(--card)', borderRadius: 14, padding: '16px 20px', marginBottom: 12 }}>
                <div style={{
                  color: '#f59e0b', fontSize: 12, fontWeight: 700, marginBottom: 10,
                  textTransform: 'uppercase', letterSpacing: 0.5,
                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                }}>
                  🌡 Режим прессования
                  {job.mode_source === 'device' && (
                    <span style={{
                      background: '#0ea5e920', color: '#0ea5e9', borderRadius: 6,
                      padding: '1px 8px', fontSize: 10.5, letterSpacing: 0,
                    }}>со станка</span>
                  )}
                  {job.mode_source === 'operator' && (
                    <span style={{
                      background: 'var(--bg)', color: 'var(--dim)', borderRadius: 6,
                      padding: '1px 8px', fontSize: 10.5, letterSpacing: 0,
                    }}>записал оператор</span>
                  )}
                </div>
                {/* Названия — как в документах по качеству у заказчика. Единицы
                    те же, что были: меняются только подписи. */}
                <InfoRow label="Температура прессования" value={job.temperature ? `${job.temperature} °C` : null} color="#f59e0b" />
                <InfoRow label="Время прессования" value={job.hold_time_seconds ? `${job.hold_time_seconds} сек` : null} color="#f59e0b" />
                <InfoRow label="Удельное давление, МПа" value={job.pressure} color="#f59e0b" />
                <InfoRow label="Температура в производственном помещении" value={job.air_temp ? `${job.air_temp} °C` : null} />
                <InfoRow label="Относительная влажность воздуха" value={job.humidity ? `${job.humidity} %` : null} />
                {/* Завод идёт перед номером — так читают накладную, и так же
                    стоят поля в окне начала смены у пресса. */}
                <InfoRow label="Завод плиты основы" value={job.dsp_maker} />
                <InfoRow label="Партия плиты основы" value={job.dsp_batch} />
                <InfoRow label="Завод плёнки" value={job.film_maker} />
                <InfoRow label="Номер паллеты плёнки" value={job.film_pallet} />
                {job.note && <InfoRow label="Примечание" value={job.note} />}
              </div>
            )}

            {/* Израсходованное сырьё */}
            {job?.raw_materials?.length > 0 && (
              <div style={{ background: 'var(--card)', borderRadius: 14, padding: '16px 20px', marginBottom: 16 }}>
                <div style={{ color: '#10b981', fontSize: 12, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>📦 Израсходованное сырьё</div>
                {job.raw_materials.map((m, i) => (
                  <div key={i} style={{ padding: '6px 0', borderBottom: i < job.raw_materials.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ color: 'var(--text)', fontSize: 13, fontWeight: 600 }}>{m.name}</div>
                    <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 2, display: 'flex', gap: 16 }}>
                      {m.supplier !== '—' && <span>Поставщик: <b style={{ color: 'var(--muted)' }}>{m.supplier}</b></span>}
                      {m.batch_no && <span>Партия: <b style={{ color: 'var(--muted)' }}>{m.batch_no}</b></span>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* QR — в самом низу, после всей истории листа.
                Бирка на листе рвётся, затирается и остаётся на обрезке, а лист
                идёт дальше по цеху. С открытой страницы код можно переснять
                телефоном или показать соседу, не бегая к принтеру.
                Белая подложка не зависит от темы: сканеры читают тёмное по
                светлому, и на тёмном фоне код бы просто не взялся. */}
            {data.qr_image && (
              <div style={{
                background: 'var(--card)', borderRadius: 14, padding: '18px 20px',
                marginBottom: 12, textAlign: 'center',
              }}>
                <div style={{
                  color: 'var(--muted)', fontSize: 12, fontWeight: 700,
                  marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5,
                }}>🏷 Код листа</div>
                <img src={data.qr_image} alt={data.code || data.qr_short}
                  style={{
                    width: 180, height: 180, background: '#fff',
                    padding: 10, borderRadius: 10, display: 'block', margin: '0 auto',
                  }} />
                <div style={{
                  color: 'var(--text)', fontSize: 16, fontWeight: 800,
                  letterSpacing: 1, marginTop: 12,
                }}>{data.code || data.qr_short}</div>
                <div style={{ color: 'var(--dim)', fontSize: 11, marginTop: 4, wordBreak: 'break-all' }}>
                  {data.qr_link}
                </div>
              </div>
            )}

            {data.locked && (
              <div style={{ textAlign: 'center', marginBottom: 12 }}>
                {!who && (
                  <button onClick={() => { setShowLogin(true); window.scrollTo(0, 0); }} style={{
                    width: '100%', minHeight: 48, borderRadius: 10, border: '1px solid var(--accent)',
                    background: 'none', color: 'var(--accent)', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                  }}>{t('Войти для полного паспорта')}</button>
                )}
              </div>
            )}

            {data.locked && data.has_password && (
              <div style={{ textAlign: 'center', marginBottom: 12 }}>
                {!showKey ? (
                  <button onClick={() => setShowKey(true)} style={{
                    background: 'none', border: 'none', color: 'var(--dim)',
                    fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline',
                  }}>Вход для сотрудников</button>
                ) : (
                  <form onSubmit={unlock} style={{ display: 'flex', gap: 10 }}>
                    <input type="password" value={keyInput} autoComplete="off" autoFocus
                      onChange={e => setKeyInput(e.target.value)} placeholder="Пароль"
                      style={{ flex: 1, minWidth: 0, padding: '10px 12px', background: 'var(--input)', border: '1px solid var(--border)', borderRadius: 10, color: 'var(--text)', fontSize: 14, outline: 'none' }} />
                    <button type="submit" disabled={!keyInput.trim() || loading}
                      style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                      Войти
                    </button>
                  </form>
                )}
                {data.key_error && (
                  <div style={{ color: 'var(--danger)', fontSize: 13, marginTop: 8 }}>{data.key_error}</div>
                )}
              </div>
            )}

            <button
              onClick={() => { setData(null); setQr(''); setTimeout(() => inputRef.current?.focus(), 100); }}
              style={{ width: '100%', background: 'var(--input)', color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 0', fontSize: 14, cursor: 'pointer' }}
            >
              ← Новый скан
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
